/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
  IComponent,
  IComponentProps,
  msdyn365Commerce,
} from "@msdyn365-commerce/core";
import {ICartState} from "@msdyn365-commerce/global-state";
import {Coupon} from "@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g";
import {
  Button,
  format,
  getPayloadObject,
  getTelemetryAttributes,
  IPayLoad,
  ITelemetryContent,
  TelemetryConstant,
} from "@msdyn365-commerce-modules/utilities";
import * as React from "react";

import {PriceComponent} from "@msdyn365-commerce/components";
import {validateCouponAsync} from "../../../../actions/PromoCodeValidationDataActionExtension.g";
export interface IPromoCodeProps extends IComponentProps<{}> {
  cart: ICartState | undefined;
  promoCodeHeadingText: string;
  appliedPromoCodeHeadingText: string;
  removePromoAriaLabelFormat: string;
  promoPlaceholderText: string;
  promoCodeApplyButtonText: string;
  collapseTimeOut: number;
  removePromoText: string;
  invalidPromoCodeErrorText: string;
  failedToAddPromoCodeErrorText: string;
  duplicatePromoCodeErrorText: string;
  failedToRemovePromoCodeErrorText: string;
  multiPromoCodeError: string;

  /**
   * The telemetry content
   */
  telemetryContent?: ITelemetryContent;
  promoCodeApplyCallback?(): void;
  // cartId : string | undefined; //
  // couponCode : (string | undefined)[] //
}

export interface IPromoCodeComponent extends IComponent<IPromoCodeProps> {}

const PromoCodeComponentActions = {};

interface IPromoCodeState {
  isCollapseOpen: boolean;
  promoCodeInputValue: string;
  error: string;
  canApply: boolean;
}

export interface PromoAPIRequestObject {
  cartId: string | undefined;
  couponCode: string | undefined;
}
/**
 *
 * The PromoCode component renders the promocode section.
 * @extends {React.PureComponent<IRefineSubmenuProps>}
 */
class PromoCode extends React.Component<IPromoCodeProps, IPromoCodeState> {
  private readonly payLoad: IPayLoad;

  constructor(props: IPromoCodeProps, state: IPromoCodeState) {
    super(props);
    this.payLoad = getPayloadObject(
      "click",
      this.props.telemetryContent!,
      TelemetryConstant.ApplyPromoCode,
    );
    this.state = {
      isCollapseOpen: false,
      promoCodeInputValue: "",
      error: "",
      canApply: false,
    };
  }

  public shouldComponentUpdate(nextProps: IPromoCodeProps): boolean {
    if (
      this.props.appliedPromoCodeHeadingText !==
      nextProps.appliedPromoCodeHeadingText
    ) {
      return false;
    }
    return true;
  }

  public render(): JSX.Element {
    return (
      <div>
        <div className="msc-promo-code-heading">
          {this.props.promoCodeHeadingText}
        </div>
        {this._renderForm(
          this.props.promoPlaceholderText,
          this.props.promoCodeApplyButtonText,
          this.props.cart,
        )}
        <p
          className={this.state.error ? "msc-alert-danger" : ""}
          aria-live="assertive"
        >
          {this.state.error}
        </p>
        {this._renderAppliedPromoCode(this.props)}
      </div>
    );
  }

  private readonly _onInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const error = e.target.value === "" ? "" : this.state.error;
    this.setState({
      promoCodeInputValue: e.target.value.trim(),
      error,
      canApply: !!e.target.value,
    });
  };

  private readonly _applyPromotion = (cartState: ICartState | undefined) => {
    if (!cartState || !cartState.cart) {
      return;
    }
    const appliedPromo = this.state.promoCodeInputValue;
    cartState
      .addPromoCode({promoCode: appliedPromo})
      .then((result) => {
        if (result.status === "SUCCESS") {
          // Show success text
          this.setState({promoCodeInputValue: "", error: "", canApply: false});
        } else if (result.substatus === "ALREADYADDED") {
          this.setState({error: this.props.duplicatePromoCodeErrorText});
        } else {
          this.setState({error: this.props.invalidPromoCodeErrorText});
        }
      })
      .catch((error) => {
        this.setState({error: this.props.failedToAddPromoCodeErrorText});
      });
  };

  //VSI-Customization-Talha start BugFix-2665
  private readonly _checkIfAppliedPromoCodeMoreThanOne = (
    props: IPromoCodeProps,
    cartState: ICartState | undefined,
  ) => {
    let couponCodes =
      props && props.cart && props.cart.cart && props.cart.cart.Coupons;
    if (couponCodes && couponCodes?.length > 0) {
      this._handlePromoCall(props, cartState);
    } else {
      this._applyPromotion(cartState);
    }
  };

  private readonly _handlePromoCall = async (
    props?: IPromoCodeProps,
    cartState?: ICartState | undefined,
  ) => {
    let cartId = props?.cart?.cart.Id ?? `${props?.cart?.cart.Id}`;

    let couponCodes =
      props && props.cart && props.cart.cart && props.cart.cart.Coupons;
    const appliedPromo = this.state.promoCodeInputValue;

    if (couponCodes?.length !== 0) {
      validateCouponAsync(
        {callerContext: this.props.context.actionContext},
        cartId,
        appliedPromo,
      )
        .then((res) => {
          if (res.Status === true) {
            this._applyPromotion(cartState);
          } else {
            this.setState({error: this.props.multiPromoCodeError});
          }
        })
        .catch((err) => {
          return err;
        });
    }
  };
  //VSI-Customization-Talha end BugFix-2665

  private readonly _renderForm = (
    promoPlaceholderText: string,
    promoCodeApplyButtonText: string,
    cartState: ICartState | undefined,
  ) => {
    const _onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      this._applyPromotion(cartState);
    };
    const _applyPromotion = (event?: React.MouseEvent<HTMLElement>) => {
      // this._applyPromotion(cartState);
      this._checkIfAppliedPromoCodeMoreThanOne(this.props, cartState); //VSI-Customization BugFix-2665
    };
    const attributes = getTelemetryAttributes(
      this.props.telemetryContent!,
      this.payLoad,
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        _applyPromotion();
      }
    };
    return (
      <form onSubmit={_onSubmit} className="msc-promo-code__form-container">
        <div className="msc-promo-code__group">
          <input
            className="msc-promo-code__input-box"
            aria-label={promoPlaceholderText}
            onChange={this._onInputChange}
            value={this.state.promoCodeInputValue}
            placeholder={promoPlaceholderText}
            onKeyDown={handleKeyDown}
          />
          <Button
            title={promoCodeApplyButtonText}
            className="msc-promo-code__apply-btn btn"
            onClick={_applyPromotion}
            disabled={!this.state.canApply}
            {...attributes}
          >
            {promoCodeApplyButtonText}
          </Button>
        </div>
      </form>
    );
  };

  private readonly _removePromotion = (
    cartState: ICartState | undefined,
    event: React.MouseEvent,
  ) => {
    if (!cartState) {
      return;
    }
    const code = event.currentTarget.getAttribute("data-value") || "";
    cartState
      .removePromoCodes({
        promoCodes: [code],
      })
      .then((result) => {
        if (result.status === "SUCCESS") {
          this.setState({error: ""});
        }
      })
      .catch(() => {
        this.setState({error: this.props.failedToRemovePromoCodeErrorText});
      });
  };

  private readonly _calculateDiscount = (
    code: string,
    cartState: ICartState | undefined,
  ) => {
    if (
      !cartState ||
      !cartState.cart ||
      !cartState.cart.CartLines ||
      cartState.cart.CartLines.length === 0 ||
      !code
    ) {
      return;
    }
    let discountAmount = 0;
    for (const line of cartState.cart.CartLines) {
      if (line.DiscountLines) {
        for (const discountLine of line.DiscountLines) {
          if (discountLine.DiscountCode === code) {
            discountAmount += discountLine.DiscountCost!;
          }
        }
      }
    }
    return discountAmount * -1;
  };

  private readonly _renderAppliedPromoCode = (props: IPromoCodeProps) => {
    if (
      !props.cart ||
      !props.cart.cart ||
      !props.cart.cart.Coupons ||
      !(props.cart.cart.Coupons.length > 0)
    ) {
      return;
    }
    const _removePromotion = (event: React.MouseEvent<HTMLElement>) => {
      this._removePromotion(props.cart, event);
    };
    let totalCouponDiscountAmount = 0;
    for (const coupon of props.cart.cart.Coupons) {
      const discountAmount = this._calculateDiscount(
        coupon.Code ?? "",
        props.cart,
      );
      if (discountAmount) {
        totalCouponDiscountAmount += discountAmount;
      }
    }

    return (
      <>
        <div className="msc-promo-code__discount">
          <div className="msc-promo-code__discount-heading">
            {this.props.appliedPromoCodeHeadingText}
          </div>
          <PriceComponent
            data={{
              price: {
                CustomerContextualPrice: totalCouponDiscountAmount,
              },
            }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            className="msc-promo-code__discount-value"
          />
        </div>
        {props.cart.cart.Coupons.map((coupon: Coupon) => {
          const ariaLabel = props.removePromoAriaLabelFormat
            ? format(
                props.removePromoAriaLabelFormat,
                props.removePromoText,
                coupon.Code,
              )
            : "";

          return (
            <div key={coupon.Code} className="msc-promo-code__line-container">
              <div className="msc-promo-code__line-value">
                {coupon.Code}
                {` `}
                <PriceComponent
                  data={{
                    price: {
                      CustomerContextualPrice: this._calculateDiscount(
                        coupon.Code || "",
                        props.cart,
                      ),
                    },
                  }}
                  context={props.context}
                  id={props.id}
                  typeName={props.typeName}
                  className="msc-promo-code__line-discount-value"
                />
              </div>
              <Button
                title={props.removePromoText}
                className="msc-promo-code__line__btn-remove"
                onClick={_removePromotion}
                data-value={coupon.Code}
                aria-label={ariaLabel}
              >
                {props.removePromoText}
              </Button>
            </div>
          );
        })}
      </>
    );
  };
}

export const PromoCodeComponent: React.FunctionComponent<IPromoCodeProps> =
  //@ts-expect-error
  msdyn365Commerce.createComponentOverride<IPromoCodeComponent>("PromoCode", {
    component: PromoCode,
    ...PromoCodeComponentActions,
  });

export default PromoCodeComponent;
